/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 *
 * Contenido del sheet de filtros de Facturas (solo se usa dentro de
 * FilterSheetModal). Los pickers abren MasterFilterSheetModal anidados.
 */
import React, { useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import type { ExpenseCategoryRow } from '../domain/expenseTypes';
import type { ExpenseDueStatusFilter, ExpensePaymentStatusFilter, ExpenseSortBy } from '../domain/expenseTypes';
import { EXPENSE_DUE_FILTER_OPTIONS, EXPENSE_PAYMENT_FILTER_OPTIONS, EXPENSE_PERIOD_PRESETS, EXPENSE_SORT_OPTIONS } from '../application/expensesList.constants'
import type { ExpensePeriodPreset } from '../application/expensesList.constants';
import { InventoryIsoDateField } from '../../shared/ui/InventoryIsoDateField';
import { MasterFilterSheetModal } from '../../shared/filters/MasterFilterSheetModal'
import { OperationalFilterFieldLabel, OperationalFilterPickerRow } from '../../shared/filters/OperationalFilterPrimitives'

type SupplierOption = { id: string; name: string };

type Props = {
  periodPreset: ExpensePeriodPreset;
  onPeriodPresetChange: (value: ExpensePeriodPreset) => void;
  customFromDate: string;
  customToDate: string;
  onCustomFromDateChange: (value: string) => void;
  onCustomToDateChange: (value: string) => void;
  paymentStatusFilter: ExpensePaymentStatusFilter;
  onPaymentStatusFilterChange: (value: ExpensePaymentStatusFilter) => void;
  categoryFilter: string | null;
  onCategoryFilterChange: (value: string | null) => void;
  categories: ExpenseCategoryRow[];
  supplierFilter: string | null;
  onSupplierFilterChange: (value: string | null) => void;
  suppliers: SupplierOption[];
  sortBy: ExpenseSortBy;
  onSortByChange: (value: ExpenseSortBy) => void;
  dueStatusFilter: ExpenseDueStatusFilter;
  onDueStatusFilterChange: (value: ExpenseDueStatusFilter) => void;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
};

export function ExpensesFiltersPanel({
  periodPreset,
  onPeriodPresetChange,
  customFromDate,
  customToDate,
  onCustomFromDateChange,
  onCustomToDateChange,
  paymentStatusFilter,
  onPaymentStatusFilterChange,
  categoryFilter,
  onCategoryFilterChange,
  categories,
  supplierFilter,
  onSupplierFilterChange,
  suppliers,
  sortBy,
  onSortByChange,
  dueStatusFilter,
  onDueStatusFilterChange,
}: Props) {
  const [periodSheetOpen, setPeriodSheetOpen] = useState(false);
  const [paymentSheetOpen, setPaymentSheetOpen] = useState(false);
  const [categorySheetOpen, setCategorySheetOpen] = useState(false);
  const [supplierSheetOpen, setSupplierSheetOpen] = useState(false);
  const [sortSheetOpen, setSortSheetOpen] = useState(false);
  const [dueSheetOpen, setDueSheetOpen] = useState(false);

  const periodPickerLabel = useMemo(
    () => EXPENSE_PERIOD_PRESETS.find((p) => p.id === periodPreset)?.label ?? '30 días',
    [periodPreset]
  );

  const paymentPickerLabel = useMemo(
    () => EXPENSE_PAYMENT_FILTER_OPTIONS.find((o) => o.value === paymentStatusFilter)?.label ?? 'Todos',
    [paymentStatusFilter]
  );

  const categoryPickerLabel = useMemo(() => {
    if (!categoryFilter) return 'Todas';
    return categories.find((c) => c.id === categoryFilter)?.name ?? 'Categoría';
  }, [categoryFilter, categories]);

  const supplierLabel = useMemo(() => {
    if (!supplierFilter) return 'Todos';
    return suppliers.find((s) => s.id === supplierFilter)?.name ?? 'Proveedor';
  }, [supplierFilter, suppliers]);

  const sortLabel = useMemo(
    () => EXPENSE_SORT_OPTIONS.find((o) => o.value === sortBy)?.label ?? 'Fecha',
    [sortBy]
  );

  const dueLabel = useMemo(
    () => EXPENSE_DUE_FILTER_OPTIONS.find((o) => o.value === dueStatusFilter)?.label ?? 'Todas',
    [dueStatusFilter]
  );


  const periodSheetOptions = useMemo(
    () => EXPENSE_PERIOD_PRESETS.map((p) => ({ id: p.id, name: p.label })),
    []
  );

  const paymentSheetOptions = useMemo(
    () => EXPENSE_PAYMENT_FILTER_OPTIONS.map((o) => ({ id: o.value, name: o.label })),
    []
  );

  const categorySheetOptions = useMemo(
    () => categories.map((c) => ({ id: c.id, name: c.name })),
    [categories]
  );

  const sortSheetOptions = useMemo(
    () => EXPENSE_SORT_OPTIONS.map((o) => ({ id: o.value, name: o.label })),
    []
  );

  const dueSheetOptions = useMemo(
    () => EXPENSE_DUE_FILTER_OPTIONS.map((o) => ({ id: o.value, name: o.label })),
    []
  );

  const customDateRow =
    periodPreset === 'custom' ? (
      <View style={styles.customDateRow}>
        <View style={styles.customDateField}>
          <InventoryIsoDateField
            value={customFromDate}
            onChange={onCustomFromDateChange}
            emptyLabel="Desde"
            ariaLabel="Fecha desde"
            showClearButton={false}
          />
        </View>
        <View style={styles.customDateField}>
          <InventoryIsoDateField
            value={customToDate}
            onChange={onCustomToDateChange}
            emptyLabel="Hasta"
            ariaLabel="Fecha hasta"
            showClearButton={false}
          />
        </View>
      </View>
    ) : null;

  return (
    <>
      <View style={styles.sheetBody}>
        <View style={styles.sheetSection}>
          <OperationalFilterFieldLabel>Período</OperationalFilterFieldLabel>
          <OperationalFilterPickerRow
            layout="stack"
            label="Período"
            valueLabel={periodPickerLabel}
            active={periodPreset !== '30d'}
            onPress={() => setPeriodSheetOpen(true)}
          />
          {customDateRow}
        </View>
        <View style={styles.sheetSection}>
          <OperationalFilterFieldLabel>Detalle</OperationalFilterFieldLabel>
          <OperationalFilterPickerRow
            layout="stack"
            label="Estado de pago"
            valueLabel={paymentPickerLabel}
            active={paymentStatusFilter !== 'all'}
            onPress={() => setPaymentSheetOpen(true)}
          />
          <OperationalFilterPickerRow
            layout="stack"
            label="Vencimiento"
            valueLabel={dueLabel}
            active={dueStatusFilter !== 'all'}
            onPress={() => setDueSheetOpen(true)}
          />
          <OperationalFilterPickerRow
            layout="stack"
            label="Categoría"
            valueLabel={categoryPickerLabel}
            active={categoryFilter != null}
            onPress={() => setCategorySheetOpen(true)}
          />
          <OperationalFilterPickerRow
            layout="stack"
            label="Proveedor"
            valueLabel={supplierLabel}
            active={supplierFilter != null}
            onPress={() => setSupplierSheetOpen(true)}
          />
          <OperationalFilterPickerRow
            layout="stack"
            label="Orden"
            valueLabel={sortLabel}
            active={sortBy !== 'expense_date_desc'}
            onPress={() => setSortSheetOpen(true)}
          />
        </View>
      </View>

      <MasterFilterSheetModal
        visible={periodSheetOpen}
        title="Período"
        options={periodSheetOptions}
        selectedId={periodPreset}
        onSelect={(id) => {
          if (id) onPeriodPresetChange(id as ExpensePeriodPreset);
        }}
        onClose={() => setPeriodSheetOpen(false)}
        showAllOption={false}
        appearance="ticket"
      />

      <MasterFilterSheetModal
        visible={paymentSheetOpen}
        title="Estado de pago"
        options={paymentSheetOptions}
        selectedId={paymentStatusFilter}
        onSelect={(id) => {
          if (id) onPaymentStatusFilterChange(id as ExpensePaymentStatusFilter);
        }}
        onClose={() => setPaymentSheetOpen(false)}
        showAllOption={false}
        appearance="ticket"
      />

      <MasterFilterSheetModal
        visible={categorySheetOpen}
        title="Categoría"
        options={categorySheetOptions}
        selectedId={categoryFilter}
        onSelect={(id) => onCategoryFilterChange(id)}
        onClose={() => setCategorySheetOpen(false)}
        allOptionLabel="Todas"
        appearance="ticket"
      />

      <MasterFilterSheetModal
        visible={supplierSheetOpen}
        title="Proveedor"
        options={suppliers.map((s) => ({ id: s.id, name: s.name }))}
        selectedId={supplierFilter}
        onSelect={(id) => onSupplierFilterChange(id)}
        onClose={() => setSupplierSheetOpen(false)}
        allOptionLabel="Todos"
        appearance="ticket"
      />

      <MasterFilterSheetModal
        visible={dueSheetOpen}
        title="Vencimiento"
        options={dueSheetOptions}
        selectedId={dueStatusFilter}
        onSelect={(id) => {
          if (id) onDueStatusFilterChange(id as ExpenseDueStatusFilter);
        }}
        onClose={() => setDueSheetOpen(false)}
        showAllOption={false}
        appearance="ticket"
      />

      <MasterFilterSheetModal
        visible={sortSheetOpen}
        title="Ordenar gastos"
        options={sortSheetOptions}
        selectedId={sortBy}
        onSelect={(id) => {
          if (id) onSortByChange(id as ExpenseSortBy);
        }}
        onClose={() => setSortSheetOpen(false)}
        showAllOption={false}
        appearance="ticket"
      />
    </>
  );
}

const styles = StyleSheet.create({
  sheetBody: {
    gap: 18,
  },
  sheetSection: {
    gap: 8,
  },
  customDateRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 4,
    alignSelf: 'stretch',
  },
  customDateField: {
    flex: 1,
    minWidth: 0,
    maxWidth: 220,
  },
});
